import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/kit";
import { ROLE_LABELS } from "@/lib/constants";
import { Plus, Building2, Users, Pencil, Trash2, Lock, Unlock, Save, X, Settings, Globe, Loader2, Sparkles } from "lucide-react";

const PLANS = ["Inicial", "Profissional", "Agência", "Empresarial"];
const DRAFT_KEY = "pf_brand_draft";

function BrandEditModal({ brand, onClose, onSaved }) {
  const [f, setF] = useState({ name: brand.name || "", niche: brand.niche || "", site: brand.site || "", color: brand.color || "#3457d5" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await client.put(`/brands/${brand.id}`, f); toast.success("Marca atualizada"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="brand-edit-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 pf-fade-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-slate-900">Editar marca</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="brand-edit-close"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Nome"><Input data-testid="edit-brand-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Nicho"><Input data-testid="edit-brand-niche" value={f.niche} onChange={(e) => setF({ ...f, niche: e.target.value })} /></Field>
          <Field label="Site"><Input data-testid="edit-brand-site" value={f.site} onChange={(e) => setF({ ...f, site: e.target.value })} /></Field>
          <Field label="Cor"><input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-10 w-full rounded-xl border border-slate-200" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="edit-brand-save" onClick={save} disabled={saving}><Save size={15} /> Salvar</Btn>
        </div>
      </div>
    </div>
  );
}

export default function Empresas() {
  const { activeOrgId, activeOrg, orgs, reload, role, setActiveOrg } = useAuth();
  const [brands, setBrands] = useState([]);
  const [members, setMembers] = useState([]);
  const [nb, setNb] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || { name: "", niche: "", site: "", tone: "Acolhedor e profissional", color: "#3457d5" }; }
    catch { return { name: "", niche: "", site: "", tone: "Acolhedor e profissional", color: "#3457d5" }; }
  });
  const [nm, setNm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [newOrg, setNewOrg] = useState("");
  const [editBrand, setEditBrand] = useState(null);
  const [orgForm, setOrgForm] = useState({ name: "", plan: "Inicial" });
  const [analyzing, setAnalyzing] = useState(false);
  const [palette, setPalette] = useState([]);
  const [suggestedDna, setSuggestedDna] = useState(null);
  const canManage = ["super_admin", "owner", "admin"].includes(role);
  const isOwner = ["super_admin", "owner"].includes(role);

  const load = () => {
    if (!activeOrgId) return;
    client.get("/brands").then((r) => setBrands(r.data));
    client.get(`/organizations/${activeOrgId}/members`).then((r) => setMembers(r.data)).catch(() => setMembers([]));
  };
  useEffect(load, [activeOrgId]);
  useEffect(() => { if (activeOrg) setOrgForm({ name: activeOrg.name, plan: activeOrg.plan || "Starter" }); }, [activeOrg?.id]);
  useEffect(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(nb)); }, [nb]);

  const createBrand = async () => {
    if (!nb.name.trim()) return toast.error("Informe o nome da marca");
    try {
      const { data: created } = await client.post("/brands", nb);
      if (suggestedDna && created?.id) {
        await client.put(`/brands/${created.id}`, {
          mission: suggestedDna.mission || "", audience: suggestedDna.audience || "",
          keywords: suggestedDna.keywords || "",
          tone_options: suggestedDna.tone ? [suggestedDna.tone] : [],
          phone: suggestedDna.phone || "", email: suggestedDna.email || "",
        });
      }
      toast.success("Marca criada");
      setNb({ name: "", niche: "", site: "", tone: "Acolhedor e profissional", color: "#3457d5" });
      setPalette([]); setSuggestedDna(null);
      localStorage.removeItem(DRAFT_KEY);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const analyzeSite = async () => {
    if (!nb.site.trim()) return toast.error("Informe o site primeiro");
    setAnalyzing(true);
    try {
      const { data } = await client.post("/brands/analyze-site", { url: nb.site });
      setNb((p) => ({ ...p, name: p.name || data.name || "", niche: data.niche || p.niche,
                     tone: data.tone || p.tone, color: data.color || p.color, site: data.site || p.site }));
      setPalette(data.palette || []);
      setSuggestedDna({ mission: data.mission, audience: data.audience, keywords: data.keywords, tone: data.tone, phone: data.phone, email: data.email });
      toast.success(data.ai_used ? "Site lido — marca sugerida pela IA" : "Site lido");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };
  const toggleBrand = async (b) => {
    try { await client.patch(`/brands/${b.id}/status?active=${!(b.active !== false)}`); toast.success(b.active !== false ? "Marca bloqueada" : "Marca desbloqueada"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteBrand = async (b) => {
    if (!window.confirm(`Excluir a marca "${b.name}" e todo o conteúdo dela? Esta ação é permanente.`)) return;
    try { await client.delete(`/brands/${b.id}`); toast.success("Marca excluída"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addMember = async () => {
    try { await client.post(`/organizations/${activeOrgId}/members`, nm); toast.success("Membro adicionado"); setNm({ name: "", email: "", password: "", role: "editor" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeRole = async (m, newRole) => {
    try { await client.patch(`/organizations/${activeOrgId}/members/${m.id}?role=${newRole}`); toast.success("Papel atualizado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleMember = async (m) => {
    try { await client.patch(`/organizations/${activeOrgId}/members/${m.id}/block?blocked=${!m.blocked}`); toast.success(m.blocked ? "Membro desbloqueado" : "Membro bloqueado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeMember = async (m) => {
    if (!window.confirm(`Remover ${m.name} da organização?`)) return;
    try { await client.delete(`/organizations/${activeOrgId}/members/${m.id}`); toast.success("Membro removido"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveOrg = async () => {
    try { await client.patch(`/organizations/${activeOrgId}`, orgForm); toast.success("Empresa atualizada"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteOrg = async () => {
    if (!window.confirm(`Excluir a empresa "${activeOrg?.name}" e TODOS os seus dados? Esta ação é permanente.`)) return;
    try {
      await client.delete(`/organizations/${activeOrgId}`);
      toast.success("Empresa excluída");
      const other = orgs.find((o) => o.id !== activeOrgId);
      setActiveOrg(other ? other.id : null);
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createOrg = async () => {
    if (!newOrg.trim()) return;
    try { const { data } = await client.post("/organizations", { name: newOrg }); toast.success("Organização criada"); setNewOrg(""); await reload(); setActiveOrg(data.id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Empresas / Multiempresa" subtitle="Gerencie a empresa, marcas e a equipe com controle de papéis (RBAC)." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Empresa atual */}
          {canManage && activeOrg && (
            <Card>
              <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Settings size={18} className="text-[#3457d5]" /> Configurações da empresa</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nome da empresa"><Input data-testid="org-name" value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} /></Field>
                <Field label="Plano"><Select data-testid="org-plan" value={orgForm.plan} onChange={(e) => setOrgForm({ ...orgForm, plan: e.target.value })}>{PLANS.map((p) => <option key={p}>{p}</option>)}</Select></Field>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <Btn data-testid="save-org-btn" onClick={saveOrg}><Save size={15} /> Salvar empresa</Btn>
                {isOwner && <Btn variant="danger" data-testid="delete-org-btn" onClick={deleteOrg}><Trash2 size={15} /> Excluir empresa</Btn>}
              </div>
            </Card>
          )}

          {/* Marcas */}
          <Card>
            <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Building2 size={18} className="text-[#3457d5]" /> Marcas de {activeOrg?.name}</h2>
            {brands.length ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {brands.map((b) => {
                  const blocked = b.active === false;
                  return (
                    <div key={b.id} className={`rounded-xl border p-4 ${blocked ? "border-rose-200 bg-rose-50/40" : "border-slate-200"}`} data-testid={`brand-card-${b.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg shrink-0" style={{ background: b.color }} />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-800 truncate flex items-center gap-2">{b.name} {blocked && <span className="text-[10px] bg-rose-100 text-rose-700 rounded-full px-2 py-0.5">Bloqueada</span>}</div>
                          <div className="text-xs text-slate-500 truncate">{b.niche || "Sem nicho definido"}</div>
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1.5 mt-3">
                          <Btn variant="secondary" className="!px-2.5 !py-1.5" data-testid={`brand-edit-${b.id}`} onClick={() => setEditBrand(b)} title="Editar"><Pencil size={14} /></Btn>
                          <Btn variant="secondary" className="!px-2.5 !py-1.5" data-testid={`brand-block-${b.id}`} onClick={() => toggleBrand(b)} title={blocked ? "Desbloquear" : "Bloquear"}>{blocked ? <Unlock size={14} /> : <Lock size={14} />}</Btn>
                          <Btn variant="danger" className="!px-2.5 !py-1.5" data-testid={`brand-delete-${b.id}`} onClick={() => deleteBrand(b)} title="Excluir"><Trash2 size={14} /></Btn>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : <Empty>Nenhuma marca ainda.</Empty>}
          </Card>

          {/* Equipe */}
          <Card>
            <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Users size={18} className="text-[#3457d5]" /> Equipe & Papéis</h2>
            <div className="space-y-2 mb-4">
              {members.map((m) => (
                <div key={m.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 ${m.blocked ? "border-rose-200 bg-rose-50/40" : "border-slate-100"}`} data-testid={`member-${m.id}`}>
                  <div className="min-w-0"><div className="text-sm font-medium text-slate-800 flex items-center gap-2">{m.name} {m.blocked && <span className="text-[10px] bg-rose-100 text-rose-700 rounded-full px-2 py-0.5">Bloqueado</span>}</div><div className="text-xs text-slate-500">{m.email}</div></div>
                  <div className="flex items-center gap-1.5">
                    {canManage && m.role !== "owner" ? (
                      <Select className="!py-1.5 !px-2 text-xs w-auto" data-testid={`member-role-${m.id}`} value={m.role} onChange={(e) => changeRole(m, e.target.value)}>
                        {["admin", "marketing", "editor", "approver", "viewer"].map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </Select>
                    ) : <span className="text-xs font-semibold text-[#3457d5] bg-blue-50 rounded-full px-2.5 py-0.5">{ROLE_LABELS[m.role] || m.role}</span>}
                    {canManage && m.role !== "owner" && (
                      <>
                        <Btn variant="secondary" className="!px-2 !py-1.5" data-testid={`member-block-${m.id}`} onClick={() => toggleMember(m)} title={m.blocked ? "Desbloquear" : "Bloquear"}>{m.blocked ? <Unlock size={13} /> : <Lock size={13} />}</Btn>
                        <Btn variant="danger" className="!px-2 !py-1.5" data-testid={`member-remove-${m.id}`} onClick={() => removeMember(m)} title="Remover"><Trash2 size={13} /></Btn>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {!members.length && <div className="text-sm text-slate-400">Sem membros listados.</div>}
            </div>
            {canManage && (
              <div className="grid sm:grid-cols-2 gap-3 border-t border-slate-100 pt-4">
                <Field label="Nome"><Input data-testid="member-name" value={nm.name} onChange={(e) => setNm({ ...nm, name: e.target.value })} /></Field>
                <Field label="E-mail"><Input data-testid="member-email" value={nm.email} onChange={(e) => setNm({ ...nm, email: e.target.value })} /></Field>
                <Field label="Senha inicial"><Input data-testid="member-password" value={nm.password} onChange={(e) => setNm({ ...nm, password: e.target.value })} /></Field>
                <Field label="Papel">
                  <Select data-testid="member-role" value={nm.role} onChange={(e) => setNm({ ...nm, role: e.target.value })}>
                    {["admin", "marketing", "editor", "approver", "viewer"].map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </Select>
                </Field>
                <div className="sm:col-span-2"><Btn data-testid="add-member-btn" onClick={addMember}><Plus size={16} /> Adicionar membro</Btn></div>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h3 className="font-display font-bold mb-3">Nova marca</h3>
            <div className="space-y-3">
              <Field label="Site (leitura automática)">
                <div className="flex gap-2">
                  <Input data-testid="brand-site" className="flex-1" value={nb.site} onChange={(e) => setNb({ ...nb, site: e.target.value })} placeholder="www.suaempresa.com.br" />
                  <Btn variant="secondary" className="!px-3 shrink-0" data-testid="analyze-site-btn" onClick={analyzeSite} disabled={analyzing} title="Ler site e sugerir marca">
                    {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />}
                  </Btn>
                </div>
              </Field>
              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Sparkles size={12} className="text-[#3457d5]" /> Informe o site e clique no globo: lemos cores, nome e a IA sugere nicho, tom e DNA.</p>
              <Field label="Nome"><Input data-testid="brand-name" value={nb.name} onChange={(e) => setNb({ ...nb, name: e.target.value })} /></Field>
              <Field label="Nicho"><Input data-testid="brand-niche" value={nb.niche} onChange={(e) => setNb({ ...nb, niche: e.target.value })} /></Field>
              <Field label="Tom de voz"><Input data-testid="brand-tone" value={nb.tone} onChange={(e) => setNb({ ...nb, tone: e.target.value })} /></Field>
              <Field label="Cor da marca"><input type="color" data-testid="brand-color" value={nb.color} onChange={(e) => setNb({ ...nb, color: e.target.value })} className="h-10 w-full rounded-xl border border-slate-200" /></Field>
              {palette.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1.5">Paleta detectada (clique para usar):</div>
                  <div className="flex flex-wrap gap-2" data-testid="brand-palette">
                    {palette.map((c) => (
                      <button key={c} type="button" onClick={() => setNb({ ...nb, color: c })} title={c}
                        className={`h-8 w-8 rounded-lg border-2 ${nb.color === c ? "border-slate-800" : "border-white shadow"}`} style={{ background: c }} />
                    ))}
                  </div>
                </div>
              )}
              {suggestedDna?.mission && <p className="text-[11px] text-emerald-600">✓ DNA sugerido (missão, público e palavras-chave) será salvo ao criar.</p>}
              <Btn data-testid="create-brand-btn" className="w-full" onClick={createBrand} disabled={!canManage}><Plus size={16} /> Criar marca</Btn>
              {!canManage && <p className="text-xs text-slate-400">Seu papel ({ROLE_LABELS[role]}) não permite criar marcas.</p>}
              <p className="text-[11px] text-slate-400">O que você digitar fica salvo automaticamente até criar.</p>
            </div>
          </Card>
          <Card>
            <h3 className="font-display font-bold mb-3">Nova organização</h3>
            <Field label="Nome da organização"><Input data-testid="new-org-name" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} /></Field>
            <Btn variant="secondary" className="w-full mt-3" data-testid="create-org-btn" onClick={createOrg}>Criar organização</Btn>
            <p className="text-xs text-slate-400 mt-2">Você possui {orgs.length} organização(ões).</p>
          </Card>
        </div>
      </div>

      {editBrand && <BrandEditModal brand={editBrand} onClose={() => setEditBrand(null)} onSaved={load} />}
    </div>
  );
}
